import { useState, useCallback } from "react";
import { Dayjs } from "dayjs";
import { PaymentMethod, PaymentType } from "../types";

interface UsePaymentMethodReturn {
  method: PaymentMethod;
  selectedPayments: string[];
  paymentType: PaymentType;
  referenceNumber: string;
  dateReceived: Dayjs | null;
  markAsPaid: boolean;
  manualEntryNotes: string;
  manualEntryTransactionId: string;
  setMethod: (method: PaymentMethod) => void;
  setSelectedPayments: (selectedPayments: string[]) => void;
  toggleSelectedPayment: (paymentId: string) => void;
  setPaymentType: (paymentType: PaymentType) => void;
  setReferenceNumber: (referenceNumber: string) => void;
  setDateReceived: (dateReceived: Dayjs | null) => void;
  setMarkAsPaid: (markAsPaid: boolean) => void;
  setManualEntryNotes: (notes: string) => void;
  setManualEntryTransactionId: (transactionId: string) => void;
}

export const usePaymentMethod = (initial?: Partial<UsePaymentMethodReturn>): UsePaymentMethodReturn => {
  const [method, setMethodState] = useState<PaymentMethod>(initial?.method || "online");
  const [selectedPayments, setSelectedPaymentsState] = useState<string[]>(
    initial?.selectedPayments || []
  );
  const [paymentType, setPaymentTypeState] = useState<PaymentType>(initial?.paymentType || "Bank Transfer");
  const [referenceNumber, setReferenceNumberState] = useState<string>(initial?.referenceNumber || "");
  const [dateReceived, setDateReceivedState] = useState<Dayjs | null>(initial?.dateReceived || null);
  const [markAsPaid, setMarkAsPaidState] = useState<boolean>(initial?.markAsPaid || false);
  const [manualEntryNotes, setManualEntryNotesState] = useState<string>(initial?.manualEntryNotes || "");
  const [manualEntryTransactionId, setManualEntryTransactionIdState] = useState<string>(initial?.manualEntryTransactionId || "");

  const setMethod = useCallback((value: PaymentMethod) => {
    setMethodState(value);
  }, []);

  const setSelectedPayments = useCallback((value: string[]) => {
    setSelectedPaymentsState(value);
  }, []);

  const toggleSelectedPayment = useCallback((paymentId: string) => {
    setSelectedPaymentsState((prev) => {
      if (prev.includes(paymentId)) {
        return prev.filter((id) => id !== paymentId);
      }
      return [...prev, paymentId];
    });
  }, []);

  const setPaymentType = useCallback((value: PaymentType) => {
    setPaymentTypeState(value);
  }, []);

  const setReferenceNumber = useCallback((value: string) => {
    setReferenceNumberState(value);
  }, []);

  const setDateReceived = useCallback((value: Dayjs | null) => {
    setDateReceivedState(value);
  }, []);

  const setMarkAsPaid = useCallback((value: boolean) => {
    setMarkAsPaidState(value);
  }, []);

  const setManualEntryNotes = useCallback((value: string) => {
    setManualEntryNotesState(value);
  }, []);

  const setManualEntryTransactionId = useCallback((value: string) => {
    setManualEntryTransactionIdState(value);
  }, []);

  return {
    method,
    selectedPayments,
    paymentType,
    referenceNumber,
    dateReceived,
    markAsPaid,
    manualEntryNotes,
    manualEntryTransactionId,
    setMethod,
    setSelectedPayments,
    toggleSelectedPayment,
    setPaymentType,
    setReferenceNumber,
    setDateReceived,
    setMarkAsPaid,
    setManualEntryNotes,
    setManualEntryTransactionId,
  };
};
